import { CommonModule, NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { PreviewBlockHelper } from '../../../../../utils/preview-block-helper';
import {
  GenVideoPreviewData,
  GenVideoPreviewVideo,
} from '../../../../../models/google-template/preview-interfaces/gen-video-preview-data.interface';
import { YoutubeHelper } from '../../../../../utils/youtube-helper';
import { BasePreviewShellComponent } from '../preview-common/base-preview-shell.component';
import { PreviewVariant } from '../preview-common/preview-variant.interface';
import { GEN_VIDEO_VARIANTS } from './variants/gen-video-variants';

/**
 * Demand Gen video ad preview. All the selector mechanics live in
 * {@link BasePreviewShellComponent}; this class only supplies the two
 * type-specific pieces: the variant registry and how to build the flat
 * {@link GenVideoPreviewData} DTO from the blocks.
 *
 * To add a placement, register it in `GEN_VIDEO_VARIANTS`.
 */
@Component({
  selector: 'app-gen-video-preview',
  imports: [CommonModule, NgComponentOutlet],
  templateUrl: '../preview-common/base-preview-shell.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: '../preview-common/base-preview-shell.component.scss'
})
export class GenVideoPreviewComponent extends BasePreviewShellComponent<GenVideoPreviewData> {
  protected readonly registry: PreviewVariant[] = GEN_VIDEO_VARIANTS;

  protected buildPreviewData(): GenVideoPreviewData {
    return {
      longHeadlines: PreviewBlockHelper.collectFromBlock(
        this.blocks,
        'gen-video-long-headline',
        'GenVideoLongHeadline_',
        5,
      ),
      headlines: PreviewBlockHelper.collectFromBlock(
        this.blocks,
        'gen-video-headline',
        'GenVideoHeadline_',
        5,
      ),
      descriptions: PreviewBlockHelper.collectFromBlock(
        this.blocks,
        'gen-video-description',
        'GenVideoDescription_',
        5,
      ),
      videos: this.toVideos(
        PreviewBlockHelper.collectFromBlock(
          this.blocks,
          'gen-video-youtube-url',
          'GenVideoYoutubeUrl_',
          5,
        ),
      ),
      ctaText: PreviewBlockHelper.getVar(
        PreviewBlockHelper.findBlock(this.blocks, 'gen-video-cta-text'),
        'Text',
      ),
      url: this.toAbsoluteUrl(
        PreviewBlockHelper.getVar(
          PreviewBlockHelper.findBlock(this.blocks, 'gen-video-url'),
          'Link',
        ),
      ),
    };
  }

  /**
   * Pairs each link with its thumbnail. Derived from the video id, so it
   * costs nothing to recompute — which matters, because the shell rebuilds
   * this DTO on every change-detection pass.
   */
  private toVideos(urls: string[]): GenVideoPreviewVideo[] {
    return urls.map((url) => {
      const videoId = YoutubeHelper.extractVideoId(url);
      return {
        url: this.toAbsoluteUrl(url),
        thumbnailUrl: videoId ? YoutubeHelper.thumbnailUrl(videoId) : '',
        videoId: videoId ?? '',
      };
    });
  }

  /**
   * Prefix a scheme onto a destination URL missing one, e.g. "www.example.com"
   * -> "https://www.example.com". Without this, an `[href]` bound straight to
   * the raw block text resolves as relative to the *editor's own* origin
   * instead of navigating out to the destination.
   */
  private toAbsoluteUrl(rawUrl: string): string {
    const url = rawUrl.trim();
    if (!url) {
      return '';
    }
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
  }
}
